import React, { useEffect, useState } from "react";
import {
  Phone,
  Mail,
  MapPin,
  Calendar,
  ArrowLeft,
  User,
  ShieldCheck,
  Share2,
  Loader2,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchMarketPlaceItemById } from "../../api/marketPlace.js";
import { toast } from "react-toastify";

function ViewItem() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const mockItemFallback = {
    _id: id,
    title: "Apple MacBook Air M1 (8GB / 256GB SSD) - Space Gray",
    description:
      "Selling in excellent condition. Battery health 88%. Comes with original 30W charger, box, and bill. Perfect for coding, video editing, and college projects. No scratches or dent.",
    price: 54999,
    category: "Laptops",
    condition: "Like New",
    location: "Bansal Institute, Lucknow",
    imageUrl:
      "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?auto=format&fit=crop&w=800&q=80",
    postedDate: "Aug 28, 2026",
    owner: {
      username: "Aditya Rai",
      contact: "+91 9876543210",
      email: "aditya.rai@campusconnect.in",
    },
  };

  const fetchItem = async (itemId) => {
    try {
      setLoading(true);
      const res = await fetchMarketPlaceItemById(itemId);
      if (res && res.title) {
        setItem(res);
      } else {
        setItem(mockItemFallback);
      }
    } catch (err) {
      console.warn("Backend unavailable, loading item details fallback");
      setItem(mockItemFallback);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItem(id);
  }, [id]);

  const handleShare = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Product link copied to clipboard!");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col justify-center items-center py-20">
        <Loader2 className="w-10 h-10 text-purple-600 animate-spin mb-3" />
        <p className="text-gray-500 text-sm font-medium">Loading gadget details...</p>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col justify-center items-center p-6">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-sm border border-gray-100">
          <p className="text-gray-800 font-bold mb-2">Item Not Found</p>
          <button
            onClick={() => navigate("/marketplace")}
            className="px-5 py-2.5 bg-purple-600 text-white rounded-xl font-bold text-xs shadow-xs"
          >
            Back to MarketPlace
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/70 text-slate-800 pb-16 pt-6">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back navigation */}
        <button
          onClick={() => navigate("/marketplace")}
          className="mb-6 flex items-center space-x-2 text-xs font-bold text-gray-600 hover:text-purple-700 transition-colors"
        >
          <ArrowLeft size={16} />
          <span>Back to MarketPlace</span>
        </button>

        {/* Product Details Grid Card */}
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100/90 overflow-hidden p-6 sm:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Image Column */}
            <div className="lg:col-span-5 space-y-4">
              <div className="relative rounded-2xl overflow-hidden bg-gray-100 border border-gray-100 max-h-96">
                <img
                  src={item.imageUrl}
                  alt={item.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-md px-3 py-1 rounded-full text-xs font-bold text-purple-700 shadow-xs border border-white">
                  {item.category}
                </div>
                <div className="absolute top-3 right-3 bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-xs">
                  {item.condition}
                </div>
              </div>
            </div>

            {/* Info Column */}
            <div className="lg:col-span-7 space-y-6 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-100">
                    <ShieldCheck size={14} className="text-purple-600" />
                    <span>Campus Verified Listing</span>
                  </span>

                  <button
                    onClick={handleShare}
                    className="p-2 rounded-xl text-gray-400 hover:text-purple-600 hover:bg-purple-50 transition-colors"
                    title="Share listing"
                  >
                    <Share2 size={18} />
                  </button>
                </div>

                <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 leading-tight mb-3">
                  {item.title}
                </h1>

                <div className="text-3xl font-black text-emerald-600 mb-5">
                  ₹{item.price ? item.price.toLocaleString("en-IN") : "0"}
                </div>

                <div className="space-y-2 mb-6">
                  <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Description & Specifications
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line bg-slate-50 p-4 rounded-2xl border border-gray-100">
                    {item.description}
                  </p>
                </div>
              </div>

              {/* Seller Contact Info Card */}
              <div className="bg-slate-50 rounded-2xl p-5 border border-gray-100/90 space-y-4">
                <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Seller Information & Contact
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div className="flex items-center space-x-2.5">
                    <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-bold">
                      <User size={15} />
                    </div>
                    <div>
                      <span className="text-gray-400 block text-[10px]">Seller</span>
                      <span className="font-bold text-gray-800">
                        {item.owner?.username || "Aditya Rai"}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2.5">
                    <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold">
                      <MapPin size={15} />
                    </div>
                    <div>
                      <span className="text-gray-400 block text-[10px]">Location</span>
                      <span className="font-bold text-gray-800 truncate block max-w-[150px]">
                        {item.location || "Campus Hostel"}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2.5">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold">
                      <Phone size={15} />
                    </div>
                    <div>
                      <span className="text-gray-400 block text-[10px]">Phone</span>
                      <span className="font-bold text-gray-800">
                        {item.owner?.contact || "+91 9876543210"}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2.5">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold">
                      <Calendar size={15} />
                    </div>
                    <div>
                      <span className="text-gray-400 block text-[10px]">Posted Date</span>
                      <span className="font-bold text-gray-800">
                        {item.postedDate || "Recent"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Call / Email Action CTAs */}
                <div className="flex space-x-3 pt-2">
                  <a
                    href={`tel:${item.owner?.contact || "9876543210"}`}
                    className="flex-1 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-bold text-xs flex items-center justify-center space-x-2 shadow-xs transition-colors"
                  >
                    <Phone size={15} />
                    <span>Call Seller</span>
                  </a>
                  <a
                    href={`mailto:${item.owner?.email || "seller@campusconnect.in"}`}
                    className="flex-1 py-3 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 rounded-xl font-bold text-xs flex items-center justify-center space-x-2 transition-colors"
                  >
                    <Mail size={15} />
                    <span>Email Seller</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ViewItem;
